import { useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { isPrinterSupported } from "../../platform/printer";
import { resolvePrinterFor, type ResolvedPrinter } from "../../platform/printerRegistry";
import { type RenderedLabel } from "../../receipt/labelRender";
import { NotePreview } from "./NotePreview";
import { PairPrinterPrompt } from "./PairPrinterPrompt";
import { printRenderedSticker, renderItemSticker } from "./printItemSticker";
import { useT } from "../../i18n/LanguageProvider";
import { t as translate } from "../../i18n/t";

/**
 * G-84 follow-up (owner 2026-09-17) — "Print barcode" for an ITEM.
 *
 * The shop receives products whose own barcode is damaged, covered by a
 * price gun, or was never printed; without a readable one the fastest route
 * to an item — point the tablet at it — is closed, and the florist is back to
 * typing a name. This prints the barcode the CRM already holds onto a sticker
 * from the same cat printer the notes come out of.
 *
 * G-100 (owner 2026-09-22) MADE THIS THE FALLBACK. With a printer paired, the
 * card's own button prints on one tap and never opens this; a florist reaches
 * it only from a browser, or from a tablet with nothing paired. It keeps its
 * print button because `PairPrinterPrompt` below can fix the second case on
 * the spot, and pairing that led nowhere would be worse than the extra tap.
 * The drawing and the sending both live in `printItemSticker.ts` now, shared
 * with the card, so the two routes cannot drift apart.
 *
 * G-99 (2026-09-21) WIDENED WHAT IT PRINTS. The caller now decides: the
 * manufacturer's EAN as before, or — when the item had none and the shop
 * issued its own — that code, `IT000042`. This panel does not choose between
 * them (`printableBarcode` in `ShopFloorItemCard` does) and does not care
 * which it was handed.
 *
 * It still needs nothing from the server: whatever is to be printed is already
 * on screen by the time this opens, so there is no call to fail and nothing to
 * get stale between the screen and the paper. G-99's issue call happens
 * BEFORE the panel, for that reason.
 *
 * NOT a retail EAN symbol. The text is encoded as Code128, which is what this
 * shop's scanner reads and what the box labels already use — and Code128 is
 * why a prefixed code works at all, since it carries letters as happily as
 * digits. Scanning the sticker returns exactly the text under the bars, so
 * the app finds the same item. It would not scan at somebody else's till,
 * which is not what it is for.
 */
export function ShopFloorItemBarcodePrint({
  itemName,
  barcode,
}: {
  itemName: string;
  barcode: string;
}) {
  const { t } = useT();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // G-97: a setter, because pairing now happens on this screen — without one
  // the button would succeed and the panel would still say nothing is paired.
  const [printer, setPrinter] = useState<ResolvedPrinter | null>(() =>
    resolvePrinterFor("notes"),
  );
  const supported = isPrinterSupported();

  /**
   * Returns the failure rather than calling `setError` from inside a memo,
   * which is a set-state-during-render: React tolerates it for the same
   * component, but it would race the print's own error state and it is not a
   * pattern to copy. A drawing that failed is a VALUE here.
   */
  const drawn: { ok: true; label: RenderedLabel } | { ok: false; cause: unknown } =
    useMemo(() => {
      try {
        return { ok: true, label: renderItemSticker({ itemName, barcode }, printer) };
      } catch (cause: unknown) {
        return { ok: false, cause };
      }
    }, [itemName, barcode, printer]);
  const rendered = drawn.ok ? drawn.label : null;
  const drawError = drawn.ok ? null : errorText(drawn.cause, "itemSticker.couldNotDraw");

  const handlePrint = async () => {
    if (!rendered || !printer) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await printRenderedSticker(printer, rendered);
      setDone(translate("itemSticker.printed"));
    } catch (err: unknown) {
      setError(errorText(err, "itemSticker.couldNotPrint"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-box-label">
      {drawError ? <div className="alert error">{drawError}</div> : null}
      {error ? <div className="alert error">{error}</div> : null}
      {rendered ? (
        <>
          <NotePreview bitmap={rendered.bitmap} />
          {rendered.layout.barcode.tooNarrow ? (
            <p className="alert error">
              {t("print.paperTooNarrow")}
            </p>
          ) : null}
          <p className="muted sf-box-label-len">
            {t("print.paperLength", { mm: rendered.lengthMm })}
          </p>
        </>
      ) : null}
      {!supported ? (
        <p className="muted sf-box-label-note">
          {t("print.fromTabletOnly")}
        </p>
      ) : !printer ? (
        <PairPrinterPrompt onPaired={setPrinter} />
      ) : (
        <button
          type="button"
          className="button shop-floor-action-btn"
          disabled={busy || !rendered}
          onClick={() => void handlePrint()}
        >
          {busy ? t("printer.printing") : t("itemSticker.printBarcode")}
        </button>
      )}
      {done ? <div className="alert success">{done}</div> : null}
    </div>
  );
}
